import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MessageSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    country: '',
    requestType: 'quote', // New field for request type
    commodity: '',
    spec: '',
    quantity: '',
    packaging: '',
    destination: '',
    timeline: '',
    certifications: '',
    email: '',
    whatsapp: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Send email to backend
    const sendEmail = async () => {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send-contact-email`, {
          ...formData,
          recipients: ['admin@spanxport.com', 'pmonga@spanxport.com', 'jroy@spanxport.com']
        });
        
        if (response.data.success) {
          toast({
            title: "Request Submitted Successfully!",
            description: "We'll respond with a structured quote within 24 hours.",
          });
        }
      } catch (error) {
        console.error('Error sending email:', error);
        toast({
          title: "Request Submitted!",
          description: "We'll respond with a structured quote within 24 hours.",
        });
      }
    };
    
    sendEmail();
    
    // Reset form
    setFormData({
      name: '',
      company: '',
      role: '',
      country: '',
      requestType: 'quote',
      commodity: '',
      spec: '',
      quantity: '',
      packaging: '',
      destination: '',
      timeline: '',
      certifications: '',
      email: '',
      whatsapp: ''
    });
  };

  return (
    <div className="w-full">
      <SEO 
        title="Contact SPANXPORT - Get Quote for Indian Exports"
        description="Contact SPANXPORT for quotes, sourcing inquiries, and trade partnerships. Connect via email or WhatsApp for premium Indian agricultural exports."
        keywords="contact SPANXPORT, export quote, sourcing inquiry, Indian exports contact, B2B trade inquiry"
        url="/contact"
      />
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                alt="SPANXPORT" 
                className="h-16 mx-auto mb-2"
              />
              <p className="text-base text-[#918f90] font-bold tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700 }}>
                Powering Intelligent Global Trade
              </p>
            </div>
            <h1 className="text-5xl font-bold mb-6">Contact SPANXPORT</h1>
            <p className="text-xl text-slate-700">
              Share your requirement—get a structured response with specs, timeline, and documentation checklist.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="p-8">
                  <h2 className="text-3xl font-bold mb-6">Request a Quote</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Company *</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="role">Role</Label>
                        <Input
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country *</Label>
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="requestType">Request Type *</Label>
                      <select
                        id="requestType"
                        name="requestType"
                        value={formData.requestType}
                        onChange={handleChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        required
                      >
                        <option value="quote">Quote Request</option>
                        <option value="eximstack">EximStack Walkthrough</option>
                        <option value="meeting">Schedule a Meeting</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="commodity">Commodity *</Label>
                      <Input
                        id="commodity"
                        name="commodity"
                        placeholder="e.g., Black Pepper, Refined Oil, Sugar"
                        value={formData.commodity}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="spec">Specification/Grade</Label>
                      <Input
                        id="spec"
                        name="spec"
                        placeholder="e.g., Grade A, 5% moisture, etc."
                        value={formData.spec}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="quantity">Quantity *</Label>
                        <Input
                          id="quantity"
                          name="quantity"
                          placeholder="e.g., 20 MT, 1 container"
                          value={formData.quantity}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="packaging">Packaging</Label>
                        <Input
                          id="packaging"
                          name="packaging"
                          placeholder="e.g., 25kg bags, bulk"
                          value={formData.packaging}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="destination">Destination Port *</Label>
                        <Input
                          id="destination"
                          name="destination"
                          placeholder="e.g., Dubai, Rotterdam"
                          value={formData.destination}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="timeline">Timeline</Label>
                        <Input
                          id="timeline"
                          name="timeline"
                          placeholder="e.g., Within 30 days"
                          value={formData.timeline}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="certifications">Required Documents/Certifications</Label>
                      <Textarea
                        id="certifications"
                        name="certifications"
                        placeholder="List any specific certificates or documents needed"
                        value={formData.certifications}
                        onChange={handleChange}
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="whatsapp">WhatsApp Number</Label>
                        <Input
                          id="whatsapp"
                          name="whatsapp"
                          type="tel"
                          placeholder="With country code"
                          value={formData.whatsapp}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-[#263776] text-white hover:bg-[#1e2a5e]">
                      Submit Quote Request
                    </Button>
                  </form>
                </Card>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <Card className="p-6">
                  <div className="flex items-start mb-4">
                    <Mail className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Email</h3>
                      <a
                        href="mailto:admin@spanxport.com"
                        className="text-blue-600 hover:underline"
                      >
                        admin@spanxport.com
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start mb-4">
                    <Phone className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-3">WhatsApp</h3>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-slate-600">India</p>
                          <a
                            href="https://wa.me/918291234503"
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            +91-829-12345-03
                          </a>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Singapore</p>
                          <a
                            href="https://wa.me/6598635879"
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            +65-986-35879
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-blue-50 border-blue-200">
                  <div className="flex items-start mb-4">
                    <MessageSquare className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Quick WhatsApp</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Send us a quick message with your requirement
                      </p>
                      <Button asChild className="w-full" variant="outline">
                        <a
                          href="https://wa.me/918291234503?text=Hello%20SPANXPORT%20-%20I%20want%20a%20quote.%20Commodity:%20____%20Spec:%20____%20Qty:%20____%20Destination:%20____"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Start WhatsApp Chat
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-3">Response Time</h3>
                  <p className="text-sm text-slate-600">
                    We typically respond within 24 hours with a structured quote including specifications, timeline, and documentation checklist.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">What Happens Next?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  1
                </div>
                <h3 className="font-semibold mb-2">We Review</h3>
                <p className="text-sm text-slate-600">
                  Your requirement is reviewed by our commodity specialists
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  2
                </div>
                <h3 className="font-semibold mb-2">You Receive Quote</h3>
                <p className="text-sm text-slate-600">
                  Detailed quote with specs, pricing, timeline, and documentation needs
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  3
                </div>
                <h3 className="font-semibold mb-2">We Execute</h3>
                <p className="text-sm text-slate-600">
                  Upon agreement, we execute with EximStack documentation and tracking
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
